import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowRight, FileText, ShieldCheck, ClipboardCheck, FlaskConical,
  FolderOpen, CheckCircle2, Dna, ExternalLink, Loader2,
} from "lucide-react";

const PROVIDER_OVERVIEW_PDF = "https://www.dropbox.com/scl/fi/j8nj8lx92k11q90rzyylz/GeneID-Provider-Overview.pdf?rlkey=i41zim9uif4goiimh6vyg4c53&st=4frxmjny&dl=1";

const BADGES = ["CLIA Certified", "CAP Accredited", "Since 2012", "99.99% accuracy", "Results in 2–6 weeks"];

const STEPS = [
  { n: "01", title: "Eligibility & medical necessity", desc: "We confirm each patient meets NCCN / ACOG guideline criteria before testing." },
  { n: "02", title: "Insurance & billing", desc: "We handle prior-authorizations and bill payers directly — your team is fully supported." },
  { n: "03", title: "Pre- & post-test counseling", desc: "Board-certified genetic counselors support your patients at every step." },
  { n: "04", title: "Results to your portal", desc: "Clear, actionable reports delivered to your secure portal in 2–6 weeks." },
];

const TESTS = [
  {
    tag: "PrevenTest™ · Hereditary Cancer",
    title: "Germline cancer risk, aligned to NCCN",
    video: "",
    desc: "Germline testing for HBOC/BRCA, Lynch syndrome and 8 solid-tumor cancers. Roughly 5–10% of patients carry an actionable hereditary risk that changes screening and management. Oral sample by mouthwash rinse or swab — no blood draw.",
    chips: ["HBOC / BRCA · Lynch", "8 solid-tumor cancers", "NCCN-aligned", "Oral sample"],
    resources: [
      { label: "Test overview", url: "https://www.dropbox.com/scl/fi/9prj4oobr6xln25nnbehj/Preventest-visual-aid.pdf?rlkey=mszt9w22mqdtvwex7j9bl0j6r&st=egg4knwn&dl=1" },
      { label: "NCCN eligibility criteria", url: "https://www.dropbox.com/scl/fi/rvyd2eqwedv4qnt1psii9/GeneID-Cancer-Risk-Criteria.pdf?rlkey=6cb68mmulxqc98uz8rrn39o8z&st=pfvs5pbj&dl=1" },
      { label: "Gene panel", url: "https://www.dropbox.com/scl/fi/0m001yi0doymabfm5rvsl/Cancer-gene-panel.pdf?rlkey=1sm3zgnr3zb7runkjjrk6ydwr&st=2fzjxrj7&dl=1" },
      { label: "Sample report", url: "https://www.dropbox.com/scl/fi/go8mor780kafjkeiwcrrn/Cancer-Risk-Sample-Report.pdf?rlkey=xbo0mt2gytupwf2ssez60gora&st=jt9k9ql0&dl=1" },
    ],
  },
  {
    tag: "NewbornGeneID™ · Carrier Screening",
    title: "Expanded carrier screening for family planning",
    video: "",
    desc: "Expanded carrier screening across 62+ conditions for couples planning or early in a pregnancy, aligned to ACOG & ACMG. About 1 in 4 people are carriers without any family history. Oral sample by mouthwash rinse or swab.",
    chips: ["62+ conditions", "ACOG & ACMG", "1 in 4 are carriers", "Oral sample"],
    resources: [
      { label: "Test overview", url: "https://www.dropbox.com/scl/fi/jzwebs5nttq9gzr3lgenh/NewbornGeneID-visual-aid.pdf?rlkey=hth9reiihpzpbiwodxu4kskki&st=6h5ozmdg&dl=1" },
      { label: "Gene & disease list", url: "https://www.dropbox.com/scl/fi/92w9p05j8wbd2ybooniq8/NewbornGeneID-Gene-Disease-List.pdf?rlkey=wk6tf9gw1f3qikw29wyeyqn9k&st=36shccyg&dl=1" },
      { label: "Sample report", url: "https://www.dropbox.com/scl/fi/9k45pblkp19ngopep7d7t/Carrier-Screening-sample-report.pdf?rlkey=f5lz1rqs59xz3clno9ry6t0rm&st=ktgoq64g&dl=1" },
    ],
  },
  {
    tag: "Pharmacogenomics · PGx",
    title: "Safer prescribing, matched to the patient",
    video: "",
    desc: "Shows how a patient metabolizes 200+ medications across cardiology, psychiatry, pain and more, for safer prescribing. 75%+ of patients carry a clinically relevant drug-response variant.",
    chips: ["200+ medications", "CYP2D6 · CYP2C19", "CPIC / FDA guidance", "75%+ carry a variant"],
    resources: [
      { label: "Test overview", url: "https://www.dropbox.com/scl/fi/g19r1t23o1e6wzf98t5u9/PGx-visual-aid.pdf?rlkey=od0cjwxm5q3l522icoo8z8dbc&st=w3jxz8bk&dl=1" },
      { label: "Medication list", url: "https://www.dropbox.com/scl/fi/zbwhlul1lg90d0n4h7eci/PGx-Result-Medications-updated-4.23.2024.pdf?rlkey=ks6600vj8jyiqytxg7wrx88r8&st=jko3on90&dl=1" },
      { label: "Comprehensive sample report", url: "https://www.dropbox.com/scl/fi/th88slzkagaburozw0yqs/PGx-Comprehesive-Panel-Results-1.pdf?rlkey=gu0rpmg4vm0q0tmmdda7df0td&st=4r1by80k&dl=1" },
    ],
  },
  {
    tag: "Envisage™ · NIPT / Prenatal",
    title: "Non-invasive prenatal screening from 10 weeks",
    video: "",
    desc: "Non-invasive prenatal screening from a single maternal blood draw, as early as 10 weeks. 99%+ accuracy with a sub-1% failure rate — ACOG-recommended for all pregnancies.",
    chips: ["From 10 weeks", "Single blood draw", "99%+ accuracy", "ACOG for all pregnancies"],
    resources: [
      { label: "Test overview", url: "https://www.dropbox.com/scl/fi/ve4wg2pmvp6vyzb5uk31z/EnvisageFinal-Visual-Aid-4.28.24.pdf?rlkey=pwahwugpm5ri7489gdwqixsku&st=xb8yigsu&dl=1" },
      { label: "Sample report", url: "https://www.dropbox.com/scl/fi/91u47lwva60yfw04iu63s/NIPT-Sample-Report.pdf?rlkey=7cnvkp2v1t42h69i84jqjdpkd&st=mwrgnyav&dl=1" },
    ],
  },
  {
    tag: "Xenome™ · Whole Exome Sequencing",
    title: "Answers for complex, undiagnosed presentations",
    video: "",
    desc: "Whole exome sequencing for complex, undiagnosed presentations — autism, epilepsy, developmental delay and more — when targeted panels come back negative or the picture is unclear. ~20,000 genes analyzed, ACMG-aligned. Oral sample by mouthwash rinse or swab.",
    chips: ["~20,000 genes", "ASD · epilepsy · DD/ID", "ACMG first-tier", "Oral sample"],
    resources: [
      { label: "Provider one-pager", url: "https://www.dropbox.com/scl/fi/0yvlejmtpgnn8c7oco879/Xenome-ABA-Referral-Partner-Onepager.pdf?rlkey=pp2nngurrzywzh29yanfeh3ci&st=slo0kq89&dl=1" },
      { label: "Sample WES report", url: "https://www.dropbox.com/scl/fi/ej5frvxgbzha8bkzgbyr8/WES-report-DEMO-1.pdf?rlkey=on2907cz1sw4hg7l31t7notqk&st=8klva210&dl=1" },
    ],
  },
];

const DOCS = [
  { title: "Recommended medical-note verbiage", desc: "Copy-ready order and encounter-note language by test, with SOAP framing and medical-necessity support.", url: "https://www.dropbox.com/scl/fi/miflp0sypt0yx5fg35gno/Recommended-medical-note-verbiage.pdf?rlkey=ot7kzp0nxiszukaraq17n46ix&st=6npzk9o9&dl=1" },
  { title: "Insurance coverage by state", desc: "One page per state summarizing payer coverage and how billing works for your patients.", url: "https://www.dropbox.com/scl/fi/7wly0qcouc6j30sm4o4xj/GeneID_Insurance_Coverage_OnePagePerState.pdf?rlkey=wkukbh9igufvgpeebrk9xvytt&st=i29vwy9v&dl=1" },
  { title: "Patient flyers — English & Spanish", desc: "Waiting-room and hand-out flyers across the full test menu, in both languages.", url: "https://www.dropbox.com/scl/fi/nitkvh7goudsmud8ejj6k/Patient-Flyers-English-Spanish.pdf?rlkey=m1p5al7814w6e4id9h6ofm13d&st=se6jnavl&dl=1" },
  { title: "Full provider overview", desc: "The complete two-page summary of the portfolio, program, and partnership options.", url: PROVIDER_OVERVIEW_PDF },
];

const PARTNERS = [
  {
    level: "Lowest touch", name: "Referrals",
    desc: "You identify the patient and place a note — GeneID handles everything else directly with them.",
    bullets: ["We manage outreach, eligibility review & test-kit delivery", "In-office materials: posters, QR postcards & patient flyers", "Results delivered back through the secure provider portal"],
    best: "Practices that want continuity of care, fully supported.",
    actions: [
      { label: "Start a referral (DocuSign)", url: "https://na4.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=dc8e0d77-c199-401f-961d-9a4ea945e65e&env=na4&acct=8f6b44c4-c577-4f8a-8d67-e8ea5ea2f5ab&v=2", primary: true },
      { label: "Patient education", url: "https://github.com/GeneIDlab/patient-referrals" },
    ],
  },
  {
    level: "Full service", name: "Genetic Testing Specialist (GTS)",
    desc: "A certified Genetic Testing Specialist placed in your office, screening eligible patients right in the exam room.",
    bullets: ["Dedicated GTS with NCCN & ACOG criteria built in", "Oral sample by mouthwash rinse or swab — no blood draws", "We manage billing, authorizations & pre/post-test counseling"],
    best: "Higher-volume practices wanting a fully supported, end-to-end solution.",
    actions: [
      { label: "GTS new client enrollment", url: "https://na4.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=e8462e90-199b-4bb5-8654-af219c5075d2&env=na4&acct=8f6b44c4-c577-4f8a-8d67-e8ea5ea2f5ab&v=2", primary: true },
    ],
  },
  {
    level: "AI-powered", name: "GeneID Insights",
    desc: "AI reviews your EMR to surface patients who already meet medical-necessity criteria for testing.",
    bullets: ["Scans ICD-10 codes, medications & clinical keywords", "Curated, guideline-based reports flag who qualifies", "Co-branded outreach & ongoing monitoring"],
    best: "90-day trial · no obligation. Cancel anytime with 30-day notice — then $199/mo.",
    actions: [
      { label: "Open an Insights account (DocuSign)", url: "https://na4.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=65561931-565f-436a-9f9e-2f0b1c505afb&env=na4&acct=8f6b44c4-c577-4f8a-8d67-e8ea5ea2f5ab&v=2", primary: true },
      { label: "Explore the Insights hub", url: "https://geneidlab.github.io/GeneID-Insights/" },
    ],
  },
];

const ext = (url) => window.open(url, "_blank", "noopener,noreferrer");

// Accepts a full Vimeo URL or a bare ID and returns a player embed URL (or "" if none).
const vimeoSrc = (video) => {
  if (!video) return "";
  const id = String(video).match(/(\d{6,})/);
  return id ? `https://player.vimeo.com/video/${id[1]}?badge=0&autopause=0&player_id=0&app_id=58479` : "";
};

// Pre-fill the DocuSign "GeneID Rep" signer role as AFHA Team on every enrollment form.
const REP_ROLE = "GeneID Rep";
const REP_NAME = "AFHA Team";
const REP_EMAIL = "support@afhateam.com";
const withRep = (url) => {
  if (!url.includes("docusign.net")) return url;
  const enc = encodeURIComponent;
  return `${url}&${enc(REP_ROLE + "_UserName")}=${enc(REP_NAME)}&${enc(REP_ROLE + "_Email")}=${enc(REP_EMAIL)}`;
};

const EMPTY_LEAD = { practice_name: "", office_manager: "", address: "", email: "", phone: "", npi: "", patient_load: "" };

export default function ProviderHub() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [dialog, setDialog] = useState(null); // { url, partner }
  const [lead, setLead] = useState(EMPTY_LEAD);
  const [sending, setSending] = useState(false);

  const openEnroll = (partner, url) => { setLead(EMPTY_LEAD); setDialog({ url, partner }); };
  const setL = (k) => (e) => setLead({ ...lead, [k]: e.target.value });

  const submitAndContinue = async (e) => {
    e.preventDefault();
    setSending(true);
    const destination = dialog.url;
    try {
      const refCode = params.get("ref") || localStorage.getItem("afha_ref") || "";
      await api.post("/leads", { ...lead, partner_type: dialog.partner, rep_code: refCode });
      toast.success("Details saved — continuing to enrollment…");
      setDialog(null);
      setLead(EMPTY_LEAD);
      ext(withRep(destination));
    } catch (err) {
      toast.error("Please complete the required fields and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="provider-hub-page">
      <Navbar />

      {/* HERO */}
      <section className="bg-navy-deep text-white afha-grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot-light mb-5">Provider Hub · Predict · Protect · Personalize</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl">
            One place for every GeneID test, report, and <span className="text-patriot-light">resource</span> your practice needs.
          </h1>
          <p className="mt-7 text-lg text-white/70 leading-relaxed max-w-2xl">
            A CLIA-certified, CAP-accredited lab (est. 2012) spanning hereditary cancer, carrier screening,
            pharmacogenomics, prenatal, and whole exome sequencing — with a fully supported program that
            handles eligibility, consent, insurance, and counseling for you.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Button onClick={() => ext(PROVIDER_OVERVIEW_PDF)} data-testid="hero-overview-pdf" className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 gap-2">
              <FileText className="h-4 w-4" /> Provider overview (PDF)
            </Button>
            <Button onClick={() => document.getElementById("test-library")?.scrollIntoView({ behavior: "smooth" })} variant="outline" data-testid="hero-explore" className="rounded-full border-white/30 text-white hover:bg-white hover:text-navy h-12 px-8 gap-2 bg-transparent">
              Explore the test library <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap gap-2.5">
            {BADGES.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3.5 py-1.5 text-sm text-white/80">
                <CheckCircle2 className="h-3.5 w-3.5 text-patriot-light" /> {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAM STEPS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot mb-4">Fully supported, end to end</p>
        <h2 className="font-display text-3xl lg:text-4xl text-navy tracking-tight max-w-2xl">We run the program — you keep seeing patients</h2>
        <p className="mt-4 text-muted-foreground max-w-2xl">From the first note to the final report, your team is fully supported at every step.</p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-xl border border-border bg-white p-6" data-testid={`step-${s.n}`}>
              <span className="font-display text-4xl text-patriot/30">{s.n}</span>
              <h3 className="font-semibold text-navy mt-3">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WATCH FIRST VIDEO */}
      <section className="bg-white border-y border-border">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot mb-4">Watch first</p>
          <h2 className="font-display text-3xl lg:text-4xl text-navy tracking-tight">Three tailored ways to partner with GeneID</h2>
          <p className="mt-4 text-muted-foreground">A quick overview of how your practice can work with us — then explore the full test library and resources below.</p>
          <div className="mt-8 rounded-2xl overflow-hidden border border-border shadow-lg" style={{ position: "relative", paddingBottom: "56.25%", height: 0 }} data-testid="intro-video">
            <iframe
              src="https://player.vimeo.com/video/1207441573?badge=0&autopause=0&player_id=0&app_id=58479"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              title="Three Tailored Ways to Partner with GeneID"
            />
          </div>
        </div>
      </section>

      {/* TEST LIBRARY */}
      <section id="test-library" className="bg-white border-y border-border scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot mb-4">The test library</p>
          <h2 className="font-display text-3xl lg:text-4xl text-navy tracking-tight max-w-2xl">Five tests, every resource in one place</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl">Each test links to its overview, gene panel, eligibility criteria, and a real sample report you can share with colleagues.</p>

          <div className="mt-12 space-y-6">
            {TESTS.map((t) => (
              <div key={t.tag} className="rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="test-card">
                <div className="lg:col-span-7">
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-patriot"><Dna className="h-4 w-4" /> {t.tag}</div>
                  <h3 className="font-display text-2xl text-navy mt-3">{t.title}</h3>
                  <p className="text-muted-foreground mt-3 leading-relaxed">{t.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {t.chips.map((c) => (
                      <span key={c} className="rounded-full bg-navy/5 border border-navy/10 px-3 py-1 text-xs font-medium text-navy">{c}</span>
                    ))}
                  </div>
                  {vimeoSrc(t.video) && (
                    <div className="mt-5 rounded-xl overflow-hidden border border-border shadow-sm" style={{ position: "relative", paddingBottom: "56.25%", height: 0 }} data-testid="test-video">
                      <iframe
                        src={vimeoSrc(t.video)}
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                        title={`${t.title} — training video`}
                      />
                    </div>
                  )}
                </div>
                <div className="lg:col-span-5">
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-3">Resources</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {t.resources.map((r) => (
                      <button key={r.label} onClick={() => ext(r.url)} data-testid="resource-link" className="group flex items-center gap-2.5 rounded-lg border border-border bg-white px-3.5 py-3 text-left hover:border-patriot transition-colors">
                        <FileText className="h-4 w-4 text-patriot shrink-0" />
                        <span className="text-sm font-medium text-navy leading-tight">{r.label}</span>
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOCS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot mb-4">For your practice</p>
        <h2 className="font-display text-3xl lg:text-4xl text-navy tracking-tight max-w-2xl">Documentation & patient-facing tools</h2>
        <p className="mt-4 text-muted-foreground max-w-2xl">Everything you need to order confidently and hand to patients.</p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {DOCS.map((d) => (
            <button key={d.title} onClick={() => ext(d.url)} data-testid="doc-card" className="group text-left rounded-xl border border-border bg-white p-6 hover:border-patriot transition-colors flex gap-4">
              <div className="h-11 w-11 rounded-lg bg-patriot/10 flex items-center justify-center shrink-0"><FolderOpen className="h-5 w-5 text-patriot" /></div>
              <div>
                <h3 className="font-semibold text-navy flex items-center gap-2">{d.title} <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{d.desc}</p>
                <span className="text-sm font-semibold text-patriot mt-3 inline-block">Open PDF →</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* PARTNER OPTIONS */}
      <section id="partner" className="bg-navy-deep text-white afha-grain scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot-light mb-4">Three ways to partner</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight max-w-2xl">Start with what fits your workflow</h2>
          <p className="mt-4 text-white/70 max-w-2xl">Scale up anytime — every model is fully supported.</p>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {PARTNERS.map((p) => (
              <div key={p.name} className="rounded-2xl bg-white/5 border border-white/10 p-7 flex flex-col" data-testid="partner-card">
                <span className="text-xs font-bold uppercase tracking-wider text-patriot-light">{p.level}</span>
                <h3 className="font-display text-2xl mt-2">{p.name}</h3>
                <p className="text-sm text-white/70 mt-3 leading-relaxed">{p.desc}</p>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex gap-2.5 text-sm text-white/80"><CheckCircle2 className="h-4 w-4 text-patriot-light shrink-0 mt-0.5" /> {b}</li>
                  ))}
                </ul>
                <p className="text-sm text-white/60 mt-5 border-t border-white/10 pt-4"><span className="font-semibold text-white/80">Best for:</span> {p.best}</p>
                <div className="mt-5 flex flex-col gap-2.5">
                  {p.actions.map((a) => (
                    <Button key={a.label} onClick={() => (a.primary ? openEnroll(p.name, a.url) : ext(a.url))} data-testid="partner-action"
                      className={a.primary
                        ? "rounded-full bg-patriot hover:bg-patriot-dark text-white gap-2 w-full"
                        : "rounded-full bg-transparent border border-white/30 text-white hover:bg-white hover:text-navy gap-2 w-full"}>
                      {a.label} <ArrowRight className="h-4 w-4" />
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap gap-2.5">
            {["~98% of patients pay $0", "GINA-protected", "NCCN · ACOG · ACMG", "Results in 2–6 weeks"].map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3.5 py-1.5 text-sm text-white/80">
                <ShieldCheck className="h-3.5 w-3.5 text-patriot-light" /> {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center">
        <h2 className="font-display text-3xl lg:text-4xl text-navy tracking-tight max-w-2xl mx-auto">Let's bring genetic testing into your practice.</h2>
        <p className="mt-4 text-muted-foreground max-w-xl mx-auto">We'll map the right model for your practice — fully supported, with no setup on your end.</p>
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Button onClick={() => document.getElementById("partner")?.scrollIntoView({ behavior: "smooth" })} data-testid="cta-partner" className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 gap-2">
            See partnership options <ArrowRight className="h-4 w-4" />
          </Button>
          <Button onClick={() => navigate("/#tests")} variant="outline" data-testid="cta-tests" className="rounded-full border-navy text-navy hover:bg-navy hover:text-white h-12 px-8 gap-2">
            Explore GeneID tests <FlaskConical className="h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enroll-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-navy">Before you continue{dialog ? ` — ${dialog.partner}` : ""}</DialogTitle>
            <DialogDescription>
              Tell us about your practice so an AFHA representative can support your onboarding. You'll then continue to the secure enrollment form.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAndContinue} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2" data-testid="enroll-form">
            <div className="sm:col-span-2">
              <Label htmlFor="e-practice" className="text-navy">Practice name</Label>
              <Input id="e-practice" required value={lead.practice_name} onChange={setL("practice_name")} data-testid="enroll-practice" className="mt-1.5 h-11" placeholder="Riverside Family Medicine" />
            </div>
            <div>
              <Label htmlFor="e-manager" className="text-navy">Office manager's name</Label>
              <Input id="e-manager" value={lead.office_manager} onChange={setL("office_manager")} data-testid="enroll-manager" className="mt-1.5 h-11" placeholder="Jane Doe" />
            </div>
            <div>
              <Label htmlFor="e-npi" className="text-navy">NPI <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="e-npi" value={lead.npi} onChange={setL("npi")} data-testid="enroll-npi" className="mt-1.5 h-11" placeholder="10-digit NPI" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="e-address" className="text-navy">Practice address</Label>
              <Input id="e-address" value={lead.address} onChange={setL("address")} data-testid="enroll-address" className="mt-1.5 h-11" placeholder="123 Main St, Suite 200, City, State ZIP" />
            </div>
            <div>
              <Label htmlFor="e-email" className="text-navy">Email</Label>
              <Input id="e-email" type="email" required value={lead.email} onChange={setL("email")} data-testid="enroll-email" className="mt-1.5 h-11" placeholder="office@practice.com" />
            </div>
            <div>
              <Label htmlFor="e-phone" className="text-navy">Phone</Label>
              <Input id="e-phone" required value={lead.phone} onChange={setL("phone")} data-testid="enroll-phone" className="mt-1.5 h-11" placeholder="(555) 000-0000" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="e-load" className="text-navy">Average patient load per day</Label>
              <Input id="e-load" value={lead.patient_load} onChange={setL("patient_load")} data-testid="enroll-load" className="mt-1.5 h-11" placeholder="e.g. 25" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={sending} data-testid="enroll-submit" className="w-full h-12 rounded-full bg-patriot hover:bg-patriot-dark text-white gap-2">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to enrollment <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
